import { ImmersveError } from '@/common/ImmersveError';
import { config } from '@/config';
import { useKycSessionStore } from '@/stores/kycSession.store';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';

export class CalciteClient {
  baseUrl: string;
  clientApplicationId: string;
  axiosInstance: AxiosInstance;

  constructor() {
    const testMode = config.testModeEnabled;
    this.baseUrl = testMode ? config.immersveApiBaseUrlTest : config.immersveApiBaseUrlLive;
    this.clientApplicationId = testMode
      ? config.clientApplicationIdTest
      : config.clientApplicationIdLive;
    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      timeout: config.axiosTimeout,
    });

    axiosRetry(this.axiosInstance, {
      retries: 2,
      retryCondition: (e) => {
        return isNetworkOrIdempotentRequestError(e) || !e.status || !e.response?.status;
      },
      retryDelay: axiosRetry.exponentialDelay, // delay * 0.2 * Math.random(), initial delay is 100ms, then exponentially increases
      shouldResetTimeout: true,
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    this.axiosInstance.interceptors.request.use((cfg) => {
      const kycSession = useKycSessionStore().kycSession;
      const sessionId = kycSession?.id;
      if (sessionId) {
        cfg.headers.set('X-Trace-Id', sessionId);
      }
      return cfg;
    });
  }

  async redeemAuthCode(authCode: string): Promise<SessionTokens> {
    let accessToken: string;
    let refreshToken: string;
    try {
      const { data } = await this.axiosInstance<SessionTokens>({
        method: 'POST',
        url: `/auth/token`,
        data: {
          authCode,
          clientApplicationId: this.clientApplicationId,
        },
      });

      accessToken = data.accessToken;
      refreshToken = data.refreshToken;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'RedeemAuthCodeFailed',
        cause: err as Error,
      });
    }

    if (!accessToken || !refreshToken) {
      throw ImmersveError.create({
        name: 'RedeemAuthCodeMissingTokens',
      });
    }
    return {
      accessToken,
      refreshToken,
    };
  }

  async redeemRefreshToken(props: { refreshToken: string }): Promise<SessionTokens> {
    let accessToken: string;
    let refreshToken: string;
    try {
      const { data } = await this.axiosInstance<SessionTokens>({
        method: 'POST',
        url: `/auth/token`,
        data: {
          refreshToken: props.refreshToken,
          clientApplicationId: this.clientApplicationId,
        },
      });

      accessToken = data.accessToken;
      refreshToken = data.refreshToken;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'RedeemRefreshTokenFailed',
        cause: err as Error,
      });
    }

    if (!accessToken || !refreshToken) {
      throw ImmersveError.create({
        name: 'RedeemRefreshTokenMissingTokens',
      });
    }
    return {
      accessToken,
      refreshToken,
    };
  }
}

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
}
